<template>
    <validation-observer
        v-if="bookings.client.id && texts"
        ref="bookings_settings_form"
        v-slot="{ meta, errors, handleSubmit }"
        :initialValues="initialValues"
    >
        <div class="d-flex justify-content-end pt-3 flex-wrap">
            <img
                v-for="l in activeLanguages"
                :key="l.code"
                class="select-language-icon"
                :src="`/static/common/images/flags/${l.code}.png`"
                @click="setLanguage(l.code)"
            />
        </div>
        <div class="d-flex justify-content-between flex-md-row flex-column pt-3">
            <div class="w-100 mr-md-4">
                <form-input
                    :leftIcon="['fas', 'at']"
                    label="Contact Email"
                    v-model="bookings.client.email"
                    :error="errors.email"
                    :disabled="true"
                    :rightButton="{
                        title: 'Edit Contact',
                        text: '',
                        icon: ['fas', 'edit'],
                        variant: 'primary',
                    }"
                    @handle-right-button-click="handleChangeContact"
                />
                <div>
                    <div class="d-flex flex-lg-row flex-column">
                        <form-checkbox
                            class="mr-2"
                            v-model="bookings.allow_subscriber_emails"
                            @change="validateDomain"
                            :disabled="freeEmail || spfOptionsDisabled"
                            >Send emails on your behalf</form-checkbox
                        >
                        <form-checkbox
                            v-model="bookings.send_emails_using_company_data"
                            :disabled="!bookings.allow_subscriber_emails  || spfOptionsDisable"
                            >Send emails using company name</form-checkbox
                        >
                    </div>
                </div>
            </div>
            <validation-provider v-slot="{ handleChange }" name="email_cc" rules="email" class="w-100" as="div">
                <form-input
                    :leftIcon="['fas', 'at']"
                    label="Secondary Contact Email"
                    v-model="bookings.client.email_cc"
                    :error="errors.email_cc"
                    @change="handleChange"
                />
            </validation-provider>
        </div>
        <div class="d-flex justify-content-between flex-md-row flex-column">
            <form-select
                label="Allow Degrade Invoice Status:"
                :leftIcon="['fas', 'arrow-right']"
                v-model="bookings.do_not_degrade_invoice_status"
                :options="degradeInvoiceStatusOptions"
                class="w-100 mr-md-4"
            ></form-select>
            <form-select
                label="Weekend Days:"
                class="w-100"
                :leftIcon="['fas', 'calendar-days']"
                :options="weekendDaysOptions"
                v-model="bookings.weekend_days"
            ></form-select>
        </div>
        <div class="d-flex justify-content-between flex-md-row flex-column">
            <form-select
                label="Accept Online Bookings?"
                :leftIcon="['fas', 'arrow-right']"
                v-model="bookings.calendar_widget_type"
                :options="acceptOnlineBookingsOptions"
                class="w-100 mr-md-4"
            ></form-select>
            <div class="w-100">
                <form-select
                    label="Booking Lead Time:"
                    :leftIcon="['fas', 'arrow-right']"
                    class="mb-0"
                    :options="bookingLeadTimeOptions"
                    v-model="bookings.booking_lead_time"
                ></form-select>
                <span>
                    <small>*Channel specific settings exist for this option </small>
                </span>
            </div>
        </div>
        <div class="d-flex justify-content-between flex-md-row flex-column pt-3">
            <div class="d-flex flex-md-row flex-column mr-md-4 w-100">
                <form-select
                    class="w-100 mr-2"
                    label="Allow Turnover Day Bookings:"
                    :leftIcon="['fas', 'arrow-right']"
                    v-model="bookings.allow_turnover_day"
                    :options="allowTurnoverDayBookingsOptions"
                ></form-select>
                <form-select
                    class="w-100"
                    label="Discounts Overlap Policy:"
                    :leftIcon="['fas', 'arrow-right']"
                    :options="discountsOverlapPolicyOptions"
                    v-model="bookings.auto_discount_overlap"
                ></form-select>
            </div>
            <form-select
                label="Limit Bookings To:"
                class="w-100"
                :leftIcon="['fas', 'arrow-right']"
                :options="limitBookingsOptions"
                v-model="bookings.limit_days_advance"
            ></form-select>
        </div>
        <div class="d-flex justify-content-between flex-md-row flex-column pt-3">
            <form-select
                label="Prevent search engines from indexing the Lodgix booking URL?"
                :leftIcon="['fas', 'arrow-right']"
                :options="preventSearchEnginesOptions"
                v-model="bookings.noindex"
                class="w-100 mr-md-4"
            ></form-select>
            <form-select
                class="w-100"
                label="Guest Credit Expiration"
                :leftIcon="['fas', 'arrow-right']"
                :options="guestCreditExpirationOptions"
                v-model="bookings.guest_credit_expire_months"
            ></form-select>
        </div>
        <div class="d-flex justify-content-between flex-md-row flex-column pt-3">
            <div class="w-100">
                <div class="mb-2 d-flex justify-content-between align-items-center">
                    <span> Deposit Policy ({{ currentLanguageName }})</span>
                    <b-button
                        @click="translateText('deposit_policy')"
                        v-if="selectedLanguage !== 'en'"
                        variant="default"
                        >Auto Translate</b-button
                    >
                </div>
                <validation-provider v-slot="{ handleChange }" name="deposit_policy" rules="required" class="w-100">
                    <form-textarea
                        :error="errors.deposit_policy"
                        v-model="texts.deposit_policy"
                        rows="6"
                        @input="handleChange"
                    >
                    </form-textarea>
                </validation-provider>
            </div>
        </div>
        <div class="d-flex flex-column">
            <div class="mb-2 d-flex justify-content-between align-items-center">
                <span> Online Booking Thank You Text ({{ currentLanguageName }})</span>
                <b-button
                    @click="translateText('payment_information_page_helptext')"
                    v-if="selectedLanguage !== 'en'"
                    variant="default"
                    >Auto Translate</b-button
                >
            </div>
            <validation-provider
                v-slot="{ handleChange }"
                name="payment_information_page_helptext"
                rules="required"
                class="w-100"
            >
                <form-textarea
                    v-model="texts.payment_information_page_helptext"
                    rows="6"
                    :error="errors.payment_information_page_helptext"
                    @input="handleChange"
                />
            </validation-provider>
            <b-button class="mb-2 collapse-button" variant="default" @click="visibleSMTPSettings = !visibleSMTPSettings"
                ><span>Custom SMTP</span>
                <font-awesome-icon :icon="['fas', visibleSMTPSettings ? 'arrow-up' : 'arrow-down']"
            /></b-button>
            <b-collapse v-model="visibleSMTPSettings" id="collapse-smtp" class="pt-2">
                <form-square-switch
                    class="mb-3 mr-2 smtp-settings-button"
                    v-model="bookings.use_custom_smtp_server"
                    :sync="true"
                    :labels="{ checked: 'Use custom SMTP server', unchecked: 'Use custom SMTP server' }"
                    :is-toolbar="true"
                ></form-square-switch>
                <b-row>
                    <b-col md="6">
                        <validation-provider
                            name="host"
                            :rules="bookings.use_custom_smtp_server ? 'required|validHost' : ''"
                            class="w-25"
                            v-slot="{ handleChange }"
                        >
                            <form-input
                                floating-label="SMTP Host"
                                v-model="bookings.smtp_settings.host"
                                :left-icon="['fas', 'at']"
                                :error="errors.host"
                                :disabled="!bookings.use_custom_smtp_server"
                                @change="handleChange"
                            />
                        </validation-provider>
                        <validation-provider
                            name="port"
                            :rules="bookings.use_custom_smtp_server ? 'required|validPositiveInteger' : ''"
                            class="w-25"
                            v-slot="{ handleChange }"
                        >
                            <form-input
                                floating-label="SMTP Port"
                                v-model="bookings.smtp_settings.port"
                                @change="handleChange"
                                :left-icon="['fas', 'arrow-right']"
                                :error="errors.port"
                                :disabled="!bookings.use_custom_smtp_server"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider
                            v-slot="{ handleChange }"
                            name="username"
                            :rules="bookings.use_custom_smtp_server ? 'required' : ''"
                        >
                            <form-input
                                autocomplete="new-password"
                                floating-label="SMTP Username"
                                v-model="bookings.smtp_settings.username"
                                :left-icon="['fas', 'user']"
                                :error="errors.username"
                                :disabled="!bookings.use_custom_smtp_server"
                                @change="handleChange"
                            ></form-input>
                        </validation-provider>
                        <validation-provider
                            v-slot="{ handleChange }"
                            name="password"
                            :rules="bookings.use_custom_smtp_server ? 'required' : ''"
                        >
                            <form-input
                                floating-label="SMTP Password"
                                v-model="bookings.smtp_settings.password"
                                :left-icon="['fas', 'key']"
                                :error="errors.password"
                                :disabled="!bookings.use_custom_smtp_server"
                                :secure="true"
                                @change="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
            </b-collapse>
            <b-button class="mb-2 collapse-button" variant="default" @click="visibleEmails = !visibleEmails"
                ><span> Email Signature</span>
                <font-awesome-icon :icon="['fas', visibleEmails ? 'arrow-up' : 'arrow-down']"
            /></b-button>
            <b-collapse v-model="visibleEmails" id="collapse-email" class="pt-2">
                <form-textarea rows="6" v-model="bookings.email_signature" />
                <form-checkbox v-model="bookings.append_email_signature"
                    >Append signature to manual guest correspondence?
                </form-checkbox>
            </b-collapse>
            <b-button class="mb-2 collapse-button" variant="default" @click="visibleScripts = !visibleScripts"
                ><span> Tracking Scripts </span>
                <font-awesome-icon :icon="['fas', visibleScripts ? 'arrow-up' : 'arrow-down']"
            /></b-button>
            <b-collapse id="collapse-scripts" v-model="visibleScripts" class="pt-2">
                <tracking-script
                    class="pb-3"
                    :label="
                        'Insert tracking scripts here. Script added here will be served on all Lodgix booking ' +
                        'pages and can be used to track booking page conversions.'
                    "
                    :tracking-scripts="bookings.tracking_scripts || ''"
                    :tracking-header="bookings.tracking_header || false"
                    :ga-tracking-id="bookings.ga_tracking_id || ''"
                    @input-tracking-scripts="onInputTrackingScripts"
                    @change-tracking-header="onChangeTrackingHeader"
                    @setup-cross-domain-tracking="setupCrossDomainTracking"
                    @disable-cross-domain-tracking="disableCrossDomainTracking"
                />
            </b-collapse>
            <div class="pb-3 d-flex flex-md-row flex-column">
                <!-- LOD-1944 -->
                <!-- <b-button
                    v-if="isShownResetText"
                    @click="$bvModal.show('reset-texts-modal')"
                    class="mt-3 reset-button mr-md-4"
                    variant="default"
                    >Reset Online Booking Texts</b-button
                > -->
                <b-button
                    @click="handleSubmit(_, saveSettings)"
                    class="mt-3 mr-2 save-button"
                    variant="primary"
                    :disabled="!meta.valid"
                    >Save</b-button
                >
            </div>
        </div>
        <!-- <modal id="reset-texts-modal" title="Reset Text Fields" @ok="resetTexts">
            Are you sure want to reset all onlike booking texts fields (including Cancellation and Deposit Policies) to
            default values?
        </modal> -->
        <validation-observer v-slot="{ validate }">
            <modal id="verify-smtp-modal" title="Verify Custom SMTP" @ok="validateCustomSMTP()" @shown="validate()">
                <div>
                    Verify @{{ customDomain }} through custom SMTP. An email with verification code has been sent to
                    {{ bookings.client.email }}. Please enter code below.
                </div>
                <br />
                <div>
                    <validation-provider v-slot="{ handleChange }" name="smtp_validation_code" rules="required">
                        <form-input
                            floating-label="SMTP Validation Code"
                            v-model="smtpVerificationCode"
                            :error="errors.smtp_validation_code"
                            @change="handleChange"
                        ></form-input>
                    </validation-provider>
                </div>
            </modal>
        </validation-observer>
        <validation-observer v-slot="{ meta, setValues }">
            <modal
                id="verify-primary-contact-modal"
                title="Change Contact Email"
                @shown="setValues({ email: bookings.client.email })"
                @ok.prevent="changeContactEmail"
            >
                <b-row>
                    <b-col md="9">
                        <validation-provider name="email" v-slot="{ errors, handleChange }" rules="required|email">
                            <form-input
                                v-model="newContactEmail"
                                :error="errors[0]"
                                @input="handleChange"
                                :disabled="newContactEmailVerificationCodeSent"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="3" class="ml-0 pl-0">
                        <b-button
                            @click="sendContactEmailVerificationCode"
                            title="Send verification code"
                            variant="success"
                            :disabled="newContactEmail === bookings.client.email || !meta.valid || newContactEmailVerificationCodeSent"
                            class="w-100"
                        >
                            Verify
                        </b-button>
                    </b-col>
                </b-row>
                <b-row v-if="!newContactEmailVerificationCodeSent">
                    <b-col>
                        <small>
                            Change email, click "<b>Verify</b>" and a verification code will be sent to new contact email.
                        </small>
                    </b-col>
                </b-row>
                <b-row v-if="newContactEmailVerificationCodeSent">
                    <b-col>
                        <validation-provider v-slot="{ handleChange }" name="contact_email_validation_code" rules="validInteger|required" >
                            <form-input
                                floating-label="Contact Email Validation Code"
                                v-model="newContactEmailVerificationCode"
                                :error="errors.contact_email_validation_code"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <template #modal-footer="{ ok, cancel }">
                    <div class="w-100">
                        <b-button
                            :disabled='!meta.valid || !newContactEmailVerificationCodeSent'
                            variant="primary"
                            class="float-right ml-2"
                            @click="ok"
                        >Change Contact Email</b-button>
                        <b-button variant="secondary" class="float-right ml-2" @click="cancel">Cancel</b-button>
                    </div>
                </template>                
            </modal>
        </validation-observer>        
    </validation-observer>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../api';
import page from '../generic/page/page.vue';
import FormInput from '../generic/form-input/form-input.vue';
import FormCheckbox from '../generic/form-checkbox/form-checkbox.vue';
import FormSelect from '../generic/form-select/form-select.vue';
import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
import FormTextarea from '../generic/form-textarea/form-textarea.vue';
import Modal from '../generic/modal/modal.vue';
import TrackingScript from '../common/tracking-script.vue';
import {
    ISETTINGS_ACTIVE_LANGUAGES,
    ISETTINGS_GET_TEXT,
    ISETTINGS_AUTO_TRANSLATE,
    ISETTINGS_SAVE_BOOKINGS,
    ISETTINGS_VALIDATE_DOMAIN,
    ISETTINGS_VALIDATE_SMTP,
    ISETTINGS_SET_GA_TRACKING_ID,
    ISETTINGS_VALIDATE_CONTACT_EMAIL,
    ISETTINGS_CHANGE_CONTACT_EMAIL,
} from '../../endpoints';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { formatResponseError } from '../../utils/api';
import { getCurrentInstance } from 'vue';

export default {
    components: {
        page,
        FormInput,
        FormCheckbox,
        FormSelect,
        FormTextarea,
        FormSquareSwitch,
        Modal,
        TrackingScript,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            activeLanguages: [],
            busy: null,
            tabs: [
                {
                    title: 'General',
                    route: '/app/isettings/general',
                    active: false,
                },
                {
                    title: 'Bookings',
                    route: '/app/isettings/bookings',
                    active: true,
                },
                {
                    title: 'Localization',
                    route: '/app/isettings/localization',
                    active: false,
                },
                {
                    title: 'Marketing',
                    route: '/app/isettings/marketing',
                    active: false,
                },
            ],
            selectedLanguage: null,
            texts: null,
            visibleEmails: null,
            visibleScripts: null,
            visibleSMTPSettings: null,
            spfMessage: null,
            smtpVerificationCode: null,
            validatingDomain: false,
            spfOptionsDisabled: false,
            newContactEmail: null,
            newContactEmailVerificationCodeSent: false,
            newContactEmailVerificationCode: '',
        };
    },
    computed: {
        ...mapGetters('header', ['userID']),
        ...mapGetters('iSettings', ['bookings', 'freeEmail']),
        acceptOnlineBookingsOptions() {
            return [
                { text: 'Yes', value: 'ONLINE_BOOKING' },
                { text: 'No - Show Availability Only', value: 'AVAILABILITY' },
            ];
        },
        allowTurnoverDayBookingsOptions() {
            return [{ text: 'Yes', value: true }, { text: 'No', value: false }];
        },
        bookingLeadTimeOptions() {
            let array = [];
            for (let i = 2; i <= 24; i++) {
                array.push({ text: `${i} hours`, value: i });
            }
            array.push({ text: '48 hours', value: 48 });
            array.push({ text: '72 hours', value: 72 });
            return [
                { text: 'Disabled', value: -1 },
                { text: 'Till check-in time', value: 0 },
                { text: '1 hour', value: 1 },
                ...array,
            ];
        },
        currentLanguageName() {
            return this.activeLanguages.find((item) => this.selectedLanguage === item.code)?.name || '';
        },
        discountsOverlapPolicyOptions() {
            return [
                { text: 'Apply Both', value: 0 },
                { text: 'Apply only Last Minute Discount', value: 2 },
                { text: 'Apply only Length of Stay Discount', value: 1 },
            ];
        },
        degradeInvoiceStatusOptions() {
            return [{ text: 'Yes', value: false }, { text: 'No', value: true }];
        },
        guestCreditExpirationOptions() {
            return [
                { text: 'Expires in 6 months', value: 6 },
                { text: 'Expires in 12 months', value: 12 },
                { text: 'Expires in 18 months', value: 18 },
                { text: 'Expires in 24 months', value: 24 },
            ];
        },
        limitBookingsOptions() {
            return [
                { text: 'Disabled', value: 0 },
                { text: '90 days in advance', value: 90 },
                { text: '180 days in advance', value: 180 },
                { text: '270 days in advance', value: 270 },
                { text: '365 days in advance', value: 365 },
                { text: '450 days in advance', value: 450 },
                { text: '540 days in advance', value: 540 },
                { text: '630 days in advance', value: 630 },
                { text: '720 days in advance', value: 720 },
            ];
        },
        preventSearchEnginesOptions() {
            return [{ text: 'Yes', value: true }, { text: 'No', value: false }];
        },
        weekendDaysOptions() {
            return [
                { text: 'Friday / Saturday', value: '4,5' },
                { text: 'Saturday / Sunday', value: '5,6' },
                { text: 'Thursday / Friday / Saturday', value: '3,4,5' },
                { text: 'Friday / Saturday / Sunday', value: '4,5,6' },
                {
                    text: 'Thursday / Friday / Saturday / Sunday',
                    value: '3,4,5,6',
                },
                { text: 'Thursday / Friday', value: '3,4' },
            ];
        },
        customDomain() {
            if (this.bookings.client.email !== null) {
                return this.bookings.client.email.split('@')[1];
            }
            return null;
        },
        isShownResetText() {
            return this.selectedLanguage === 'en';
        },
        initialValues() {
            return {
                deposit_policy: this.texts?.deposit_policy,
                payment_information_page_helptext: this.texts?.payment_information_page_helptext,
                email: this.bookings.client.email,
                email_cc: this.bookings.client.email_cc,
                port: this.bookings.smtp_settings.port,
                username: this.bookings.smtp_settings.username,
                password: this.bookings.smtp_settings.password,
                host: this.bookings.smtp_settings.host,
            };
        },
    },
    mounted() {
        this.fetchTexts();
    },
    methods: {
        setFormData() {
            this.$refs.bookings_settings_form.setValues({
                deposit_policy: this.texts.deposit_policy,
                payment_information_page_helptext: this.texts.payment_information_page_helptext,
                email: this.bookings.client.email,
                email_cc: this.bookings.client.email_cc,
                port: this.bookings.smtp_settings.port,
                username: this.bookings.smtp_settings.username,
                password: this.bookings.smtp_settings.password,
                host: this.bookings.smtp_settings.host,
            });
        },
        async fetchTexts() {
            try {
                await this.fetchActiveLanguages();
                const { data } = await API.get(ISETTINGS_GET_TEXT(this.selectedLanguage));
                this.texts = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch texts' });
            } finally {
                this.busy = false;
            }
        },
        async fetchActiveLanguages() {
            try {
                const { data } = await API.get(ISETTINGS_ACTIVE_LANGUAGES());
                this.activeLanguages = [{ id: null, code: 'en', name: 'English' }, ...data];
                this.selectedLanguage = this.activeLanguages[0].code;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch active languages' });
            } finally {
                this.busy = false;
            }
        },

        async validateDomain(value) {
            if (value && !this.validatingDomain) {
                this.validatingDomain = true;
                try {
                    this.infoToast({ text: 'Validating domain. Please wait ...' });
                    const { data } = await API.post(ISETTINGS_VALIDATE_DOMAIN(), {
                        email: this.bookings.client.email,
                    });
                    const domain = this.bookings.client.email.split('@')[1];
                    if (data.status === 'OK') {
                        this.successToast({ text: 'Domain successfully validated.' });
                    } else if (data.status === 'SMTP') {
                        this.$store.commit(
                            'iSettings/setBookings',
                            JSON.parse(
                                JSON.stringify({
                                    ...this.bookings,
                                    allow_subscriber_emails: false,
                                })
                            )
                        );
                        this.bookings.allow_subscriber_emails = false;
                        this.$bvModal.show('verify-smtp-modal');
                    } else if (data.status === 'SMTP_FAIL') {
                        this.$store.commit(
                            'iSettings/setBookings',
                            JSON.parse(
                                JSON.stringify({
                                    ...this.bookings,
                                    allow_subscriber_emails: false,
                                })
                            )
                        );
                        this.errorToast({
                            text: `Could not send email trough your custom SMTP server. Please check your SMTP server settings.`,
                        });
                    } else {
                        this.errorToast({
                            text: `Invalid SPF record for domain @${domain}. This option requires a valid SPF record or a custom SMTP server.`,
                        });
                        this.$store.commit(
                            'iSettings/setBookings',
                            JSON.parse(
                                JSON.stringify({
                                    ...this.bookings,
                                    allow_subscriber_emails: false,
                                })
                            )
                        );
                    }
                } catch (e) {
                    this.errorToast({ text: 'Could not validate domain' });
                } finally {
                    this.busy = false;
                    this.validatingDomain = false;
                }
            } else {
                this.$store.commit(
                    'iSettings/setBookings',
                    JSON.parse(
                        JSON.stringify({
                            ...this.bookings,
                            send_emails_using_company_data: false,
                        })
                    )
                );
                this.spfMessage = { text: '' };
            }
        },
        async validateCustomSMTP() {
            try {
                this.infoToast({ text: 'Validating SMTP. Please wait ...' });
                const { data } = await API.post(ISETTINGS_VALIDATE_SMTP(), {
                    email: this.bookings.client.email,
                    verification_code: this.smtpVerificationCode,
                });
                if (data.status === 'OK') {
                    this.successToast({ text: 'SMTP successfully verified.' });
                    this.$store.commit(
                        'iSettings/setBookings',
                        JSON.parse(
                            JSON.stringify({
                                ...this.bookings,
                                allow_subscriber_emails: true,
                            })
                        )
                    );
                } else {
                    this.errorToast({
                        text: `Invalid validation code!`,
                    });
                }
            } catch (e) {
                this.errorToast({ text: 'Could not validate domain' });
            } finally {
                this.busy = false;
            }
        },
        async translateText(field) {
            try {
                const { data } = await API.post(ISETTINGS_AUTO_TRANSLATE(), {
                    code: this.selectedLanguage,
                    field,
                });
                this.texts[field] = data;
                if (data.status === 'FAIL') {
                    this.errorToast({
                        text: 'Unable to contact Translate API. Please try again later.',
                    });
                } else {
                    this.successToast({
                        text: 'Field successfully translated.',
                    });
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not translate text' });
            }
        },
        async saveSettings() {
            try {
                this.$parent.$emit('busy', true);
                await API.post(ISETTINGS_SAVE_BOOKINGS(), {
                    language: this.selectedLanguage,
                    bookings: this.bookings,
                    texts: this.texts,
                });
                this.spfOptionsDisabled = false;
                this.successToast({ text: 'Settings successfully updated.' });
            } catch (e) {
                this.errorToast({ text: 'Could not update settings' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async setupCrossDomainTracking(gaTrackingId) {
            try {
                const { data } = await API.post(ISETTINGS_SET_GA_TRACKING_ID(), {
                    ga_tracking_id: gaTrackingId,
                });
                this.$store.commit(
                    'iSettings/setBookings',
                    JSON.parse(
                        JSON.stringify({
                            ...this.bookings,
                            ...data,
                        })
                    )
                );
                this.successToast({ text: 'GA Cross Domain Tracking successfully configured.' });
            } catch (e) {
                this.errorToast({ text: 'Could not configure GA Cross Domain Tracking.' });
            }
        },
        async disableCrossDomainTracking() {
            try {
                const { data } = await API.post(ISETTINGS_SET_GA_TRACKING_ID(), {
                    ga_tracking_id: null,
                });
                this.$store.commit(
                    'iSettings/setBookings',
                    JSON.parse(
                        JSON.stringify({
                            ...this.bookings,
                            ...data,
                        })
                    )
                );
                this.successToast({ text: 'GA Cross Domain Tracking disabled successfully.' });
            } catch (e) {
                this.errorToast({ text: 'Could not disable GA Cross Domain Tracking.' });
            }
        },
        async setLanguage(language) {
            this.selectedLanguage = language;
            const { data } = await API.get(ISETTINGS_GET_TEXT(this.selectedLanguage));
            this.texts = data;
            this.$refs.bookings_settings_form.setFieldValue('deposit_policy', this.texts.deposit_policy);
            this.$refs.bookings_settings_form.setFieldValue(
                'payment_information_page_helptext',
                this.texts.payment_information_page_helptext
            );
            this.$refs.bookings_settings_form.validate();
        },
        onInputTrackingScripts(value) {
            this.$store.commit(
                'iSettings/setBookings',
                JSON.parse(
                    JSON.stringify({
                        ...this.bookings,
                        tracking_scripts: value,
                    })
                )
            );
            // this.bookings.tracking_scripts = value;
        },
        onChangeTrackingHeader(value) {
            this.$store.commit(
                'iSettings/setBookings',
                JSON.parse(
                    JSON.stringify({
                        ...this.bookings,
                        tracking_header: value,
                    })
                )
            );
            // this.bookings.tracking_header = value;
        },
        async handleChangeContact() {
            this.newContactEmailVerificationCode = '';
            this.newContactEmailVerificationCodeSent = false;
            this.newContactEmail = this.bookings.client.email;
            this.$bvModal.show('verify-primary-contact-modal');
        },
        async sendContactEmailVerificationCode() {
            try {
                const { data } = await API.post(ISETTINGS_VALIDATE_CONTACT_EMAIL(), {
                    email: this.newContactEmail,
                });
                this.newContactEmailVerificationCodeSent = true;
                this.successToast({ text: 'Verification email successfuly sent.' });
            } catch (e) {
                this.errorToast({ text: 'Could not send verification email.' });
            }
        },
        async changeContactEmail() {
            try {
                this.$parent.$emit('busy', true);
                const { data } = await API.post(ISETTINGS_CHANGE_CONTACT_EMAIL(), {
                    code: this.newContactEmailVerificationCode,
                });
                this.successToast({ text: 'Contact email successfuly changed.' });
                this.$bvModal.hide('verify-primary-contact-modal');
                await this.$store.dispatch('iSettings/fetchSettings');
            } catch (e) {
                this.errorToast({ text: 'Invalid verification code' });
            } finally {
                this.$parent.$emit('busy', false);
            }            
        },        
    },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

@include media-breakpoint-down(sm) {
    .smtp-settings-button .v-switch-label {
        font-weight: 600 !important;
        font-size: 14px !important;
    }
}

.smtp-settings-button .v-switch-label {
    padding-top: 0.2em !important;
}

.smtp-settings-button .v-switch-core {
    height: 2.35em !important;
}

.smtp-settings-button .v-switch-button {
    top: 0.18em !important;
    height: 2em !important;
}
</style>
